import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Modal } from "components/designV2/modal/modal";
import { TextArea } from "components/designV2/textarea/textarea";
import type { TeamPublicApplicationModalState } from "./teamPublicDetailsModal.contract";
import { MotivationalLetterModalText } from "./teamPublicDetailsModal.styled";
import { DetailsMessage, ModalContent } from "../shiftDetailsModal.styled";

interface ApplicationActionModalCopy {
  readonly title: string;
  readonly body: string;
  readonly confirmLabel: string;
}

interface TeamPublicDetailsModalDialogsProps {
  readonly isMotivationalLetterModalOpen: boolean;
  readonly selectedMotivationalLetter: string;
  readonly onCloseMotivationalLetterModal: () => void;
  readonly applicationModalState: TeamPublicApplicationModalState;
  readonly applicationActionModalCopy: ApplicationActionModalCopy | null;
  readonly isApplicationActionLoading: boolean;
  readonly onCloseApplicationActionModal: () => void;
  readonly onConfirmApplicationAction: (declineReason: string) => Promise<void>;
  readonly onCloseApplicationErrorModal: () => void;
}

export const TeamPublicDetailsModalDialogs: React.FC<
  TeamPublicDetailsModalDialogsProps
> = (props) => {
  const { t } = useTranslation();
  const isConfirmModalOpen =
    props.applicationModalState.type === "confirm" &&
    props.applicationActionModalCopy != null;
  const isDeclineAction =
    isConfirmModalOpen &&
    props.applicationModalState.type === "confirm" &&
    props.applicationModalState.action.type === "decline";
  const [declineReason, setDeclineReason] = useState("");

  useEffect(() => {
    if (isConfirmModalOpen) setDeclineReason("");
  }, [isConfirmModalOpen, props.applicationModalState]);

  const confirmFooter = (
    <>
      <SecondaryButton
        label={t("General.Cancel")}
        onClick={props.onCloseApplicationActionModal}
        disabled={props.isApplicationActionLoading}
      />
      <PrimaryButton
        label={
          props.applicationActionModalCopy?.confirmLabel ?? t("General.Confirm")
        }
        onClick={() => {
          void props.onConfirmApplicationAction(declineReason);
        }}
        isLoading={props.isApplicationActionLoading}
      />
    </>
  );

  const errorFooter = (
    <PrimaryButton
      label={t("General.Confirm")}
      onClick={props.onCloseApplicationErrorModal}
    />
  );

  return (
    <>
      <Modal
        label={t("Schedule.TeamPublicMotivationalLetter")}
        isOpen={props.isMotivationalLetterModalOpen}
        onClose={props.onCloseMotivationalLetterModal}
        size="small"
        allowContentOverflow
      >
        <MotivationalLetterModalText>
          {props.selectedMotivationalLetter || "-"}
        </MotivationalLetterModalText>
      </Modal>
      <Modal
        label={props.applicationActionModalCopy?.title ?? t("General.Confirm")}
        isOpen={isConfirmModalOpen}
        onClose={props.onCloseApplicationActionModal}
        size="small"
        footer={confirmFooter}
      >
        <ModalContent>
          <DetailsMessage>
            {props.applicationActionModalCopy?.body ?? "-"}
          </DetailsMessage>
          {isDeclineAction && (
            <TextArea
              label={t("GigsCompany.NoteToApplicant")}
              placeholder={t("GigsCompany.SendANote")}
              value={declineReason}
              onChange={setDeclineReason}
              disabled={props.isApplicationActionLoading}
            />
          )}
        </ModalContent>
      </Modal>
      <Modal
        label={t("Schedule.TeamPublicApplicationActionErrorTitle")}
        isOpen={props.applicationModalState.type === "error"}
        onClose={props.onCloseApplicationErrorModal}
        size="small"
        footer={errorFooter}
      >
        <DetailsMessage>
          {t("Schedule.TeamPublicApplicationActionErrorBody")}
        </DetailsMessage>
      </Modal>
    </>
  );
};
